import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getHistories } from '../services/historyService';
import { getDisplayErrorMessage } from '../services/apiError';
import Timeline from './Timeline';
import ErrorMessage from './ErrorMessage';

const Home = ({ user }) => {
  const [histories, setHistories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedHistoryId = searchParams.get('historyId');
  const selectedEventId = searchParams.get('eventId');
  const [firstDate, setFirstDate] = useState(null);
  const [lastDate, setLastDate] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState({});
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistories = async () => {
      try {
        const data = await getHistories();
        setHistories(data);
      } catch (err) {
        setError(getDisplayErrorMessage(err));
      }
    };
    fetchHistories();
  }, [user]);

  useEffect(() => {
    if (histories.length > 0 && !selectedHistoryId) {
      setSearchParams({ historyId: histories[0].id });
    }
  }, [histories, selectedHistoryId, setSearchParams]);

  useEffect(() => {
    if (!selectedHistoryId) {
      setIsOwner(false);
      return;
    }

    const history = histories.find(h => String(h.id) === selectedHistoryId);
    if (history) {
      setIsOwner(history.isEditable || false);
    } else {
      setIsOwner(false);
    }
  }, [selectedHistoryId, histories]);

  useEffect(() => {
    if (selectedHistoryId && selectedEventId) {
      if (selectedEvents[selectedHistoryId] !== selectedEventId) {
        setSelectedEvents(prev => ({ ...prev, [selectedHistoryId]: selectedEventId }));
      }
    } else if (selectedHistoryId && !selectedEventId) {
      if (selectedEvents[selectedHistoryId]) {
        setSelectedEvents(prev => {
          const newSelected = { ...prev };
          delete newSelected[selectedHistoryId];
          return newSelected;
        });
      }
    }
  }, [selectedHistoryId, selectedEventId, selectedEvents]);

  const handleDatesFetched = useCallback((dates) => {
    setFirstDate(dates.first);
    setLastDate(dates.last);
  }, []);

  const handleHistorySelect = (id) => {
    setFirstDate(null);
    setLastDate(null);
    const params = { historyId: id };
    if (selectedEvents[id]) {
      params.eventId = selectedEvents[id];
    }
    setSearchParams(params);
  };

  const selectedHistory = histories.find(history => String(history.id) === selectedHistoryId);

  useEffect(() => {
    if (!selectedHistory) {
      document.title = 'Timelined';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Visual Timeline Creator: Build and Share Your Stories');
      }
    }
  }, [selectedHistory]);

  return (
    <div className="p-8 relative">
      <ErrorMessage message={error} onDismiss={() => setError(null)} />
      {selectedHistory && (
        <div className="mb-8 p-6 bg-white rounded shadow flex justify-between">
          <div>
            <h1 className="text-3xl font-bold">{selectedHistory.name}</h1>
            <p className="text-sm">
              {firstDate ? (
                lastDate ? `${firstDate} — ${lastDate}` : firstDate
              ) : (
                'Loading...'
              )}
            </p>
          </div>

          <button
            className="bg-white text-black text-md px-4 py-2 rounded-md border-2 hover:bg-blue-100 hover:border-blue-500 shadow-lg"
            onClick={() => navigate(`/study/${selectedHistory.id}`)}
          >
            Learn this history
          </button>
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto pb-4 mb-8">
        {histories.map(history => (
          <div
            key={history.id}
            onClick={() => handleHistorySelect(String(history.id))}
            className={`flex relative cursor-pointer p-4 rounded min-w-52 ${selectedHistoryId === String(history.id) ? 'bg-blue-100 border-2 border-blue-500' : 'bg-gray-100'}`}
          >
            {history.name}
            
            {user && history.isEditable && (
              <div className="flex flex-col justify-between absolute h-full top-0 right-0">
                {history.isPublic && (
                  <div className="text-base opacity-65"><i className="fa-solid fa-globe"></i></div>
                )}

                <div className="text-base mt-auto opacity-65"><i className="fa-regular fa-user"></i></div>
              </div>
            )}
          </div>
        ))}
      </div>

    {selectedHistory && <Timeline history={ selectedHistory } onDateFetched={handleDatesFetched} isOwner={isOwner} />}
    </div>
  )
};

export default Home;